import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { patchUser } from './authSlice'

const SLICE_NAME = 'onboarding'

/**
 * Onboarding step values:
 * 0 = Welcome (not started)
 * 1 = Profile completion
 * 2 = Create location
 * 3 = Create project
 * 4 = Task tour / introduction
 * 5 = Invite team members
 * 6 = Complete
 */
export const ONBOARDING_STEPS = {
	WELCOME: 0,
	PROFILE: 1,
	LOCATION: 2,
	PROJECT: 3,
	TASK_TOUR: 4,
	INVITE: 5,
	COMPLETE: 6,
} as const

export const STEP_LABELS = [
	'Welcome',
	'Profile',
	'Location',
	'Project',
	'Tasks',
	'Invite',
] as const

interface OnboardingState {
	currentStep: number
	isOnboarding: boolean
	isTourActive: boolean
	tourStep: number
	createdLocationId: string | null
	createdProjectId: string | null
	saving: boolean
	error: string | null
}

const initialState: OnboardingState = {
	currentStep: 0,
	isOnboarding: false,
	isTourActive: false,
	tourStep: 0,
	createdLocationId: null,
	createdProjectId: null,
	saving: false,
	error: null,
}

export const advanceOnboardingStep = createAsyncThunk(
	`${SLICE_NAME}/advanceStep`,
	async (
		{ nextStep, userId }: { nextStep: number; userId: string },
		{ dispatch, rejectWithValue },
	) => {
		try {
			const updates: any = { onboardingStep: nextStep }

			// If completing onboarding, also set firstTime to false and isCompleted to true
			if (nextStep >= ONBOARDING_STEPS.COMPLETE) {
				updates.firstTime = false
				updates.isCompleted = true
			}

			await dispatch(patchUser({ userid: userId, updates }))
			return nextStep
		} catch (error) {
			return rejectWithValue('Failed to save onboarding progress')
		}
	},
)

export const skipOnboarding = createAsyncThunk(
	`${SLICE_NAME}/skip`,
	async (userId: string, { dispatch, rejectWithValue }) => {
		try {
			await dispatch(
				patchUser({
					userid: userId,
					updates: {
						onboardingStep: ONBOARDING_STEPS.COMPLETE,
						firstTime: false,
						isCompleted: true,
					},
				}),
			)
			return ONBOARDING_STEPS.COMPLETE
		} catch (error) {
			return rejectWithValue('Failed to skip onboarding')
		}
	},
)

const onboardingSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		initializeOnboarding(state, action: PayloadAction<{ step: number; isFirstTime: boolean }>) {
			state.currentStep = action.payload.step
			state.isOnboarding = action.payload.isFirstTime && action.payload.step < ONBOARDING_STEPS.COMPLETE
		},
		setCreatedLocationId(state, action: PayloadAction<string>) {
			state.createdLocationId = action.payload
		},
		setCreatedProjectId(state, action: PayloadAction<string>) {
			state.createdProjectId = action.payload
		},
		startTour(state) {
			state.isTourActive = true
			state.tourStep = 0
		},
		advanceTour(state) {
			state.tourStep += 1
		},
		endTour(state) {
			state.isTourActive = false
			state.tourStep = 0
		},
		resetOnboarding() {
			return initialState
		},
	},
	extraReducers: builder => {
		builder
			.addCase(advanceOnboardingStep.pending, state => {
				state.saving = true
				state.error = null
			})
			.addCase(advanceOnboardingStep.fulfilled, (state, action) => {
				state.saving = false
				state.currentStep = action.payload
				if (action.payload >= ONBOARDING_STEPS.COMPLETE) {
					state.isOnboarding = false
				}
			})
			.addCase(advanceOnboardingStep.rejected, (state, action) => {
				state.saving = false
				state.error = action.payload as string
			})
			.addCase(skipOnboarding.pending, state => {
				state.saving = true
				state.error = null
			})
			.addCase(skipOnboarding.fulfilled, state => {
				state.saving = false
				state.currentStep = ONBOARDING_STEPS.COMPLETE
				state.isOnboarding = false
			})
			.addCase(skipOnboarding.rejected, (state, action) => {
				state.saving = false
				state.error = action.payload as string
			})
	},
})

export const {
	initializeOnboarding,
	setCreatedLocationId,
	setCreatedProjectId,
	startTour,
	advanceTour,
	endTour,
	resetOnboarding,
} = onboardingSlice.actions

export default onboardingSlice
